// src/contexts/ToolActionsContext.tsx
"use client";

import React, { createContext, useContext, useState, useCallback, ReactNode, useRef, useMemo } from 'react';
import type { FirstImpressionTool } from '@/types';
import type { StreamingServiceConfig } from '@/services/streamingService';
import { useStreaming } from './StreamingContext';
import { useMessageState } from './MessageStateContext';
import { useToolState } from './ToolStateContext';
import { getLastUserMessage } from '@/utils/emissaryHelpers';
import { v4 as uuidv4 } from 'uuid';

interface ToolActionsContextType {
  // Processing state
  isProcessingConfirmation: boolean;
  
  // Actions for tool confirmation workflow
  setProcessingConfirmation: (processing: boolean) => void;
  processToolConfirmation: (assistantConfig: StreamingServiceConfig) => Promise<void>;
  abortToolConfirmation: () => void;
}

const ToolActionsContext = createContext<ToolActionsContextType | null>(null);

export const useToolActions = (): ToolActionsContextType => {
  const context = useContext(ToolActionsContext);
  if (!context) {
    throw new Error('useToolActions must be used within a ToolActionsProvider');
  }
  return context;
};

interface ToolActionsProviderProps {
  children: ReactNode;
}

export const ToolActionsProvider: React.FC<ToolActionsProviderProps> = ({ children }) => {
  const [isProcessingConfirmation, setIsProcessingConfirmation] = useState(false);
  
  // Use other contexts
  const { sendToolConfirmationMessage } = useStreaming();
  const { messages } = useMessageState();
  const { confirmedTools, clearConfirmation } = useToolState();
  
  // Track processed confirmations to prevent duplicates
  const processedConfirmationRef = useRef<FirstImpressionTool[] | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const setProcessingConfirmation = useCallback((processing: boolean) => {
    setIsProcessingConfirmation(processing);
  }, []);

  // Process tool confirmation with API call
  const processToolConfirmation = useCallback(async (
    assistantConfig: StreamingServiceConfig
  ) => {
    if (!confirmedTools || confirmedTools === processedConfirmationRef.current) {
      return;
    }
    
    const lastUserMessage = getLastUserMessage(messages);
    if (!lastUserMessage) {
      console.error('No user message found to resend with confirmed tools');
      setIsProcessingConfirmation(false);
      clearConfirmation();
      return;
    }
    
    try {
      setIsProcessingConfirmation(true);
      processedConfirmationRef.current = confirmedTools;
      
      // Create a new trace ID for the confirmed request
      const traceId = `ui-confirmed-${Date.now()}-${uuidv4()}`;
      
      // Clean up any existing controller
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      
      // Create new AbortController
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;
      
      // Use the streaming context to handle tool confirmation
      await sendToolConfirmationMessage(
        lastUserMessage.content,
        confirmedTools,
        traceId,
        signal
      );
      
      setIsProcessingConfirmation(false);
      setTimeout(() => {
        clearConfirmation();
      }, 1000);
    } catch (err) {
      console.error('Error in confirmed message handling:', err);
      setIsProcessingConfirmation(false);
      setTimeout(() => {
        clearConfirmation();
      }, 2000);
    }
  }, [confirmedTools, messages, sendToolConfirmationMessage, clearConfirmation]);

  const abortToolConfirmation = useCallback(() => {
    console.log('Aborting tool confirmation');
    
    // Abort the ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    // Clear the processing state
    setIsProcessingConfirmation(false);
    clearConfirmation();
    
    // Clear the processed confirmation ref
    processedConfirmationRef.current = null;
  }, [clearConfirmation]);

  const contextValue = useMemo((): ToolActionsContextType => ({
    isProcessingConfirmation,
    setProcessingConfirmation,
    processToolConfirmation,
    abortToolConfirmation
  }), [
    isProcessingConfirmation,
    setProcessingConfirmation,
    processToolConfirmation,
    abortToolConfirmation
  ]);

  return (
    <ToolActionsContext.Provider value={contextValue}>
      {children}
    </ToolActionsContext.Provider>
  );
};